import { useEffect, useState } from 'react'
import * as THREE from 'three'
import type { FragmentsModel } from '@thatopen/fragments'

import { useAppStore } from '@core/store'
import { viewerApi } from '@viewer/lib/viewerApi'
import { escondidosPeloFantasmaEm } from '@viewer/lib/ghostContornos'
import { ouvirMudancaDeVisibilidade } from '@viewer/lib/sinalDaVisibilidade'
import { ouvirLentes } from '@viewer/lens/lensRules'
import {
  collectWorldTriangles,
  type WorldTriangleItem,
} from '@viewer/lib/worldTriangles'
import { DEFAULT_CUT_COLOR } from '@viewer/views2d/layerStyles'
import { buildCategoryMap } from '@viewer/views2d/generateForViewer'
import type { Vec3 } from '@viewer/views2d/lib/viewTypes'
import { caixaAtravessa, seccaoNoPlano } from './preenchimentoDeCorte'
import { chaveDoEstilo, estiloDoElemento, type EstiloDeCorte } from './estilosDeCorte'
import { leitorDeMateriais } from './materiaisDoModelo'
import { apanhaLentePara, lentesDasRegras, nomesDosElementos, precisaDeNomes } from './lentesNoCorte'
import { materialDaTrama } from './tramaDeCorte'
import { ligarCortesASessao } from './cortesNaSessao'

/** Preenchimento a cheio dos elementos cortados, na cena.
 *
 *  Corre sobre os planos que estão mesmo a cortar (os do renderizador:
 *  caixa de corte, planos soltos, os de um viewpoint BCF), e só sobre
 *  os elementos que se vêem: escondidos, fora do isolamento e em
 *  fantasma não se preenchem.
 *
 *  O cálculo é no CPU (ver preenchimentoDeCorte.ts) e corre um
 *  instante depois de o corte parar de mexer: enquanto se arrasta uma
 *  pega, o preenchimento antigo sai, para não ficar no sítio errado. Os
 *  triângulos ficam em cache por elemento, por isso mexer o corte outra
 *  vez só recolhe os elementos novos.
 *
 *  Cada preenchimento fica um milímetro para o lado que fica do seu
 *  plano, e os OUTROS planos cortam-no como cortam o resto: numa caixa,
 *  o preenchimento de uma face acaba nas outras. */

/** Espera depois do último movimento do corte, em ms. */
const ESPERA_MS = 250
/** Acima disto, a cache de triângulos esvazia-se antes de crescer mais. */
const TRIANGULOS_EM_CACHE_MAX = 3_000_000

interface CacheDoModelo {
  ids: number[]
  caixas: Array<{ min: Vec3; max: Vec3 }>
  origem: Vec3
  triangulos: Map<number, WorldTriangleItem>
  totalDeTriangulos: number
}

/** Por instância do modelo: reabrir um IFC com o mesmo nome é outro
 *  modelo, com outros elementos. */
const caches = new WeakMap<FragmentsModel, CacheDoModelo>()

type ModeloComCaixas = FragmentsModel & {
  getItemsIdsWithGeometry: () => Promise<number[]>
  getBoxes: (ids: number[]) => Promise<THREE.Box3[]>
}

async function cacheDe(model: FragmentsModel): Promise<CacheDoModelo | null> {
  const ja = caches.get(model)
  if (ja) return ja
  const m = model as ModeloComCaixas
  try {
    const ids = await m.getItemsIdsWithGeometry()
    const boxes = await m.getBoxes(ids)
    if (boxes.length !== ids.length) return null
    const min: Vec3 = [Infinity, Infinity, Infinity]
    const max: Vec3 = [-Infinity, -Infinity, -Infinity]
    const caixas = boxes.map((b) => {
      const c = { min: [b.min.x, b.min.y, b.min.z] as Vec3, max: [b.max.x, b.max.y, b.max.z] as Vec3 }
      for (let k = 0; k < 3; k++) {
        if (c.min[k] < min[k]) min[k] = c.min[k]
        if (c.max[k] > max[k]) max[k] = c.max[k]
      }
      return c
    })
    // Os triângulos ficam referidos ao centro do modelo: com
    // coordenadas georreferenciadas, um Float32 de mundo dentava as
    // secções (ver worldTriangles.ts).
    const origem: Vec3 = Number.isFinite(min[0])
      ? [(min[0] + max[0]) / 2, (min[1] + max[1]) / 2, (min[2] + max[2]) / 2]
      : [0, 0, 0]
    const c: CacheDoModelo = { ids, caixas, origem, triangulos: new Map(), totalDeTriangulos: 0 }
    caches.set(model, c)
    return c
  } catch (err) {
    console.warn('[corte] caixas dos elementos indisponíveis', err)
    return null
  }
}

let grupo: THREE.Group | null = null
let cenaDoGrupo: THREE.Scene | null = null
let geracao = 0

const materialDoTraco = new THREE.LineBasicMaterial({ color: DEFAULT_CUT_COLOR })

function grupoNaCena(): THREE.Group | null {
  const world = viewerApi.getWorld()
  const cena = world?.scene.three as THREE.Scene | undefined
  if (!cena) return null
  if (!grupo || cenaDoGrupo !== cena) {
    grupo = new THREE.Group()
    grupo.name = 'vn2r-preenchimento-de-corte'
    cena.add(grupo)
    cenaDoGrupo = cena
  }
  return grupo
}

function esvaziar(g: THREE.Group): void {
  for (const filho of [...g.children]) {
    g.remove(filho)
    if (filho instanceof THREE.Mesh || filho instanceof THREE.LineSegments) filho.geometry.dispose()
  }
}

function semRaio(o: THREE.Object3D): THREE.Object3D {
  // O preenchimento não se selecciona: o clique é para o elemento.
  o.raycast = () => {}
  return o
}

function juntar(partes: Float32Array[]): Float32Array {
  let n = 0
  for (const p of partes) n += p.length
  const out = new Float32Array(n)
  let k = 0
  for (const p of partes) {
    out.set(p, k)
    k += p.length
  }
  return out
}

async function calcular(minha: number): Promise<void> {
  const world = viewerApi.getWorld()
  const g = grupoNaCena()
  if (!world || !g) return
  const store = useAppStore.getState()
  const planos = (world.renderer!.three.clippingPlanes ?? []).map((p) => ({
    normal: [p.normal.x, p.normal.y, p.normal.z] as Vec3,
    // dot(n, x) + constant = 0  =>  x = -constant * n
    ponto: [-p.constant * p.normal.x, -p.constant * p.normal.y, -p.constant * p.normal.z] as Vec3,
  }))
  if (planos.length === 0) {
    esvaziar(g)
    store.setPreenchimentoInfo(null)
    return
  }
  store.setPreenchimentoInfo({ aCalcular: true, elementos: 0, abertos: 0 })
  const base = store.estiloBaseDeCorte
  const regras = store.regrasDeCorte.filter((r) => r.activa && r.valor.trim())
  const comMateriais = regras.some((r) => r.tipo === 'material')
  const lentes = lentesDasRegras(regras)
  const comNomes = precisaDeNomes(lentes)
  const pixelRatio = world.renderer!.three.getPixelRatio()
  const visiveis = new Set(store.models.filter((m) => m.visible !== false).map((m) => m.id))
  const novos: THREE.Object3D[] = []
  let elementos = 0
  let abertos = 0

  for (const [modelId, model] of viewerApi.iterModels()) {
    if (!visiveis.has(modelId)) continue
    const c = await cacheDe(model)
    if (minha !== geracao) return
    if (!c) continue
    let escondidos: Set<number>
    try {
      escondidos = new Set(await model.getItemsByVisibility(false))
    } catch {
      escondidos = new Set()
    }
    if (minha !== geracao) return
    const fantasma = escondidosPeloFantasmaEm(modelId)
    const porPlano = planos.map((plano) => {
      const ids: number[] = []
      c.ids.forEach((id, i) => {
        if (escondidos.has(id) || fantasma.has(id)) return
        if (caixaAtravessa(c.caixas[i].min, c.caixas[i].max, plano)) ids.push(id)
      })
      return ids
    })
    const faltam = [...new Set(porPlano.flat())].filter((id) => !c.triangulos.has(id))
    if (faltam.length > 0) {
      if (c.totalDeTriangulos > TRIANGULOS_EM_CACHE_MAX) {
        c.triangulos.clear()
        c.totalDeTriangulos = 0
      }
      const r = await collectWorldTriangles(model, { ids: faltam, origin: c.origem })
      if (minha !== geracao) return
      for (const item of r.items) {
        c.triangulos.set(item.localId, item)
        c.totalDeTriangulos += item.indices.length / 3
      }
    }
    // O estilo de cada elemento: a classe vem do Fragments, o material
    // do texto do IFC (só se houver regras de material), o nome dos
    // dados do elemento (só se uma lente olhar para ele).
    let classes: Map<number, string> = new Map()
    if (regras.some((r) => r.tipo === 'classe') || lentes.size > 0) {
      try {
        classes = await buildCategoryMap(model as unknown as Parameters<typeof buildCategoryMap>[0])
      } catch {
        classes = new Map()
      }
      if (minha !== geracao) return
    }
    const materiaisDe = comMateriais ? leitorDeMateriais(modelId) : () => []
    const porEstilo = new Map<string, { estilo: EstiloDeCorte; partes: Float32Array[] }>()
    const tracos: Float32Array[] = []
    const seccoes: Array<{ localId: number; posicoes: Float32Array; contornos: Float32Array }> = []
    planos.forEach((plano, i) => {
      const itens = porPlano[i]
        .map((id) => c.triangulos.get(id))
        .filter((x): x is WorldTriangleItem => !!x)
      if (itens.length === 0) return
      const local = {
        normal: plano.normal,
        ponto: [
          plano.ponto[0] - c.origem[0],
          plano.ponto[1] - c.origem[1],
          plano.ponto[2] - c.origem[2],
        ] as Vec3,
      }
      const r = seccaoNoPlano(itens, local)
      elementos += r.seccoes.length
      abertos += r.abertos
      seccoes.push(...r.seccoes)
    })
    let nomes: Map<number, string | null> = new Map()
    if (comNomes && seccoes.length > 0) {
      nomes = await nomesDosElementos(
        model as unknown as Parameters<typeof nomesDosElementos>[0],
        seccoes.map((s) => s.localId),
      )
      if (minha !== geracao) return
    }
    for (const s of seccoes) {
      const classe = classes.get(s.localId) ?? null
      const estilo =
        regras.length === 0
          ? base
          : estiloDoElemento(
              classe,
              () => materiaisDe(s.localId),
              regras,
              base,
              apanhaLentePara(lentes, { modelId, localId: s.localId, ifcClass: classe, nome: nomes.get(s.localId) ?? null }),
            )
      const k = chaveDoEstilo(estilo)
      let g = porEstilo.get(k)
      if (!g) {
        g = { estilo, partes: [] }
        porEstilo.set(k, g)
      }
      g.partes.push(s.posicoes)
      tracos.push(s.contornos)
    }
    if (tracos.length === 0) continue
    for (const { estilo, partes } of porEstilo.values()) {
      const geo = new THREE.BufferGeometry()
      geo.setAttribute('position', new THREE.BufferAttribute(juntar(partes), 3))
      const cheio = new THREE.Mesh(geo, materialDaTrama(estilo, pixelRatio))
      cheio.position.set(...c.origem)
      novos.push(semRaio(cheio))
    }
    const geoTraco = new THREE.BufferGeometry()
    geoTraco.setAttribute('position', new THREE.BufferAttribute(juntar(tracos), 3))
    const traco = new THREE.LineSegments(geoTraco, materialDoTraco)
    traco.position.set(...c.origem)
    novos.push(semRaio(traco))
  }
  if (minha !== geracao) {
    for (const o of novos) if (o instanceof THREE.Mesh || o instanceof THREE.LineSegments) o.geometry.dispose()
    return
  }
  esvaziar(g)
  for (const o of novos) g.add(o)
  g.visible = true
  store.setPreenchimentoInfo({ aCalcular: false, elementos, abertos })
  viewerApi._invalidate()
}

export function usePreenchimentoDeCorte() {
  const ligado = useAppStore((s) => s.preenchimentoDeCorte)
  const enabled = useAppStore((s) => s.sectionEnabled)
  const orientation = useAppStore((s) => s.sectionOrientation)
  const extents = useAppStore((s) => s.sectionExtents)
  // Só a geometria dos planos ligados: mudar o nome de um plano, ou
  // escondê-lo, não mexe no corte e não deve apagar o preenchimento.
  const soltos = useAppStore((s) =>
    s.planosDeCorte
      .filter((p) => p.activo)
      .map((p) => [...p.normal, ...p.ponto].join(','))
      .join(';'),
  )
  const modelos = useAppStore((s) => s.models)
  const estiloBase = useAppStore((s) => s.estiloBaseDeCorte)
  const regras = useAppStore((s) => s.regrasDeCorte)
  const [versao, setVersao] = useState(0)

  useEffect(() => ouvirMudancaDeVisibilidade(() => setVersao((v) => (v + 1) & 0xffff)), [])
  // Mudar uma lente muda o estilo dos elementos que as regras de lente apanham.
  useEffect(() => ouvirLentes(() => setVersao((v) => (v + 1) & 0xffff)), [])
  // Os planos, as combinações e as regras vão na sessão.
  useEffect(() => ligarCortesASessao(), [])

  useEffect(() => {
    const g = grupoNaCena()
    if (!g) return
    const minha = ++geracao
    // O preenchimento antigo sai já: com o corte a mexer, ficava no
    // sítio errado até ao cálculo novo.
    g.visible = false
    viewerApi._invalidate()
    if (!ligado) {
      esvaziar(g)
      useAppStore.getState().setPreenchimentoInfo(null)
      return
    }
    const t = setTimeout(() => {
      calcular(minha).catch((err) => {
        console.warn('[corte] preenchimento falhou', err)
        if (minha === geracao) useAppStore.getState().setPreenchimentoInfo(null)
      })
    }, ESPERA_MS)
    return () => clearTimeout(t)
  }, [ligado, enabled, orientation, extents, soltos, modelos, versao, estiloBase, regras])

  useEffect(() => {
    return () => {
      geracao++
      if (grupo) {
        esvaziar(grupo)
        grupo.parent?.remove(grupo)
      }
      grupo = null
      cenaDoGrupo = null
    }
  }, [])
}
